/**
 * Auto free-gift sync for "Bespoke Koi Pond Viewing Window Glass"
 * Adds 2 free units of a chosen product for every £100 of the calculated
 * price on this product's cart line.
 *
 * WHERE TO LOAD THIS:
 * Include on any page where the cart can change — product page, cart page,
 * and cart drawer (if you use one). It listens for the same events your
 * theme already fires on add/update/remove (adjust the event names in
 * bindCartEvents() to match your theme if it doesn't use these).
 *
 * ------------------------------------------------------------------
 * CONFIG — fill these in for your store
 * ------------------------------------------------------------------
 */
const AUTO_GIFT_CONFIG = {
  // The window product this rule applies to
  triggerProductId: 6146792685720,

  // Line item property keys EXACTLY as your calculator app writes them
  propKeys: {
    thickness: 'Thickness',            // option label, e.g. "25.5mm" — looked up below
    width: 'Width',                    // mm
    height: 'Height',                  // mm
    sealant: 'Include Sealant? MyPond Ms300', // string value, e.g. "4 Tubes" / "No Sealant"
  },

  // Thickness option label -> £/m² rate ("Value Per Unit" in the calculator app settings)
  thicknessRateMap: {
    '25.5mm': 350,
    '33mm': 700,
    '39mm': 750,
    '44.5mm': 800,
    '52.5mm': 900,
    '21.5mm': 280,
  },

  // Sealant option -> fixed price add-on (GBP). Match your calculator's own map.
  sealantPriceMap: {
    'No Sealant': 0,
    '4 Tubes': 55.96,
    '6 Tubes': 83.94,
    '8 Tubes': 111.92,
    '10 Tubes': 139.9,
    '12 Tubes': 169.9,
    '24 Tubes': 299.99,
  },

  // Free gift product
  giftVariantId: 56398740685179,

  // Rule: 2 free items per £100 (edit if the ratio changes)
  gbpPerTier: 100,
  giftQtyPerTier: 2,

  // Marker property so we can find/identify our own auto-added line
  giftMarkerKey: '_auto_gift',
  giftMarkerValue: 'koi-window-glass',
};

let isSyncing = false; // re-entrancy guard so our own /cart/change.js calls don't loop

/**
 * Compute the calculated price for a single cart line using the same
 * formula the calculator applies: thicknessRate * (width/1000 * height/1000) + sealant
 *
 * Both "Thickness" and the sealant property store the option LABEL the
 * customer picked (e.g. "25.5mm", "4 Tubes") — the actual £ values live in
 * the calculator app's own settings, so they're mirrored here in
 * thicknessRateMap / sealantPriceMap and looked up by label.
 */
function calculateLinePrice(properties) {
  const { thickness, width, height, sealant } = AUTO_GIFT_CONFIG.propKeys;

  const thicknessChoice = properties[thickness];
  const rate = AUTO_GIFT_CONFIG.thicknessRateMap[thicknessChoice];
  const w = parseFloat(properties[width]);
  const h = parseFloat(properties[height]);
  const sealantChoice = properties[sealant];

  if (rate == null || isNaN(rate) || isNaN(w) || isNaN(h)) {
    console.warn('Auto-gift: could not calculate price for line', properties);
    return 0;
  }

  const glassPrice = rate * ((w / 1000) * (h / 1000));
  const sealantPrice = AUTO_GIFT_CONFIG.sealantPriceMap[sealantChoice] || 0;

  return glassPrice + sealantPrice;
}

/**
 * Fetch current cart and figure out:
 * - total calculated price across all matching trigger-product lines
 * - current gift line (if any) and its quantity
 */
async function getCartState() {
  const cart = await fetch('/cart.js').then((r) => r.json());

  let triggerTotal = 0;
  let giftLine = null;
  let giftLineIndex = -1;

  cart.items.forEach((item, idx) => {
    if (item.product_id === AUTO_GIFT_CONFIG.triggerProductId) {
      triggerTotal += calculateLinePrice(item.properties || {});
    }
    if (
      item.properties &&
      item.properties[AUTO_GIFT_CONFIG.giftMarkerKey] === AUTO_GIFT_CONFIG.giftMarkerValue
    ) {
      giftLine = item;
      giftLineIndex = idx + 1; // cart line numbers are 1-indexed
    }
  });

  return { cart, triggerTotal, giftLine, giftLineIndex };
}

/**
 * Reconcile the gift line quantity against the current calculated total.
 */
async function syncGift() {
  console.log('test');
  if (isSyncing) return;
  isSyncing = true;

  try {
    const { triggerTotal, giftLine, giftLineIndex } = await getCartState();

    const tiers = Math.floor(triggerTotal / AUTO_GIFT_CONFIG.gbpPerTier);
    const targetQty = tiers * AUTO_GIFT_CONFIG.giftQtyPerTier;

    if (targetQty === 0 && giftLine) {
      // Remove gift line entirely
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: giftLineIndex, quantity: 0 }),
      });
    } else if (targetQty > 0 && !giftLine) {
      // Add gift line for the first time
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: AUTO_GIFT_CONFIG.giftVariantId,
              quantity: targetQty,
              properties: {
                [AUTO_GIFT_CONFIG.giftMarkerKey]: AUTO_GIFT_CONFIG.giftMarkerValue,
              },
            },
          ],
        }),
      });
    } else if (targetQty > 0 && giftLine && giftLine.quantity !== targetQty) {
      // Adjust existing gift line quantity
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: giftLineIndex, quantity: targetQty }),
      });
    }

    document.dispatchEvent(new CustomEvent('auto-gift:synced', { detail: { triggerTotal, targetQty } }));
  } catch (err) {
    console.error('Auto-gift sync failed:', err);
  } finally {
    isSyncing = false;
  }
}

/**
 * Hook into whatever cart events your theme fires. Adjust these to match —
 * common patterns shown below.
 */
function bindCartEvents() {
  // Standard cart AJAX API fires no native events, so most themes dispatch
  // their own. Cover the common ones; remove/add as needed for your theme.
  document.addEventListener('cart:updated', syncGift);
  document.addEventListener('cart:refresh', syncGift);
  document.addEventListener('product:added', syncGift);

  // Fallback: if your calculator app dispatches its own "price updated" event
  // after add-to-cart, hook that too, e.g.:
  // document.addEventListener('koi-calculator:added-to-cart', syncGift);

  // Run once on page load in case cart already contains the trigger product
  syncGift();
}

document.addEventListener('DOMContentLoaded', bindCartEvents);